import { useCallback, useEffect, useState } from 'react';

import type { GlobalMessageBoxNotification } from '~/amplienceTypes/schemas/imported/global-message-box-notification-schema';
import { storageAvailable } from '~/utilities/helpers';
import { convertCESTStringToDate } from '~/utilities/parsers';

const STORAGE_KEY = 'prevMessageBoxNotification';
// Keeps every copy of this hook (header bell, mobile TLN drawer row) in step within the page.
const READ_EVENT = 'messageBoxNotificationRead';
// Longest delay `setTimeout` supports (~24.8 days); a longer one would fire immediately.
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * - `unread`: the message is active and not opened yet;
 * - `read`: the message was opened (now or in an earlier visit);
 * - `expired`: it was unread and its end date passed while the page stayed open (`trackExpiry`);
 * - `inactive`: there is no active message (outside its period when the page loaded).
 */
export type MessageBoxNotificationStatus = 'unread' | 'read' | 'expired' | 'inactive';

type LocalStorageMessageType = {
  startTime: number;
  endTime: number;
  messageTitle: string;
  messageText: string;
  messageLink: string;
  linkLabel: string;
};

/**
 * The message last marked as read, or `null` when none is stored or the stored value is unreadable
 * (truncated write, manual edit, older format): treated as unread rather than crashing the header.
 */
const readStoredMessage = (): Partial<LocalStorageMessageType> | null => {
  if (!storageAvailable('localStorage')) {
    return null;
  }

  try {
    const value: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');

    return value && typeof value === 'object' ? value : null;
  } catch {
    return null;
  }
};

type MessageBoxNotificationState = {
  /** 1 while the active message is unread, 0 otherwise (same rule as the header icon). */
  messageCount: number;
  status: MessageBoxNotificationStatus;
  title: string;
  text: string;
  link: string;
  buttonLabel: string;
  /** Marks the active message as read (count 0, remembered in localStorage). */
  markAsRead: () => void;
};

type MessageBoxNotificationOptions = {
  /**
   * Also expire an unread message when its end date passes while the page stays open, as a reload
   * would (status `expired`, count 0, empty text). Used by the mobile TLN drawer row only.
   */
  trackExpiry?: boolean;
};

/**
 * Message box notification state shared by the header icon and the mobile navigation row: the
 * message is active between its start and end date and counts as unread until it is opened.
 */
export const useMessageBoxNotification = (
  {
    messageTitle,
    messageText,
    messageLink,
    startDate,
    endDate,
    linkLabel,
    emptyText,
  }: Partial<GlobalMessageBoxNotification> = {},
  { trackExpiry = false }: MessageBoxNotificationOptions = {}
): MessageBoxNotificationState => {
  const [status, setStatus] = useState<MessageBoxNotificationStatus>('inactive');
  // The message ended while the page stayed open (`trackExpiry`): it stays hidden, even once the
  // expired row is opened (status `read`), as after a reload.
  const [ended, setEnded] = useState(false);

  useEffect(() => {
    const startTime = convertCESTStringToDate(startDate);
    const endTime = convertCESTStringToDate(endDate);

    setEnded(false);

    if (
      !startTime ||
      !endTime ||
      Date.now() < startTime.getTime() ||
      Date.now() > endTime.getTime()
    ) {
      setStatus('inactive');

      return;
    }

    const prevMessageInfo = readStoredMessage();
    const isRead =
      !!prevMessageInfo &&
      prevMessageInfo.startTime === startTime.getTime() &&
      prevMessageInfo.endTime === endTime.getTime() &&
      prevMessageInfo.messageTitle === messageTitle &&
      prevMessageInfo.messageText === messageText &&
      prevMessageInfo.messageLink === messageLink &&
      prevMessageInfo.linkLabel === linkLabel;

    setStatus(isRead ? 'read' : 'unread');
  }, [endDate, linkLabel, messageLink, messageText, messageTitle, startDate]);

  useEffect(() => {
    const endTime = convertCESTStringToDate(endDate);

    if (!trackExpiry || status !== 'unread' || !endTime) {
      return undefined;
    }

    const delay = endTime.getTime() - Date.now();

    if (delay > MAX_TIMEOUT_MS) {
      return undefined;
    }

    // Same state as after a reload once the message ended, but remembered as `expired`.
    const timer = setTimeout(() => {
      setEnded(true);
      setStatus('expired');
    }, Math.max(delay, 0));

    return () => clearTimeout(timer);
  }, [endDate, status, trackExpiry]);

  useEffect(() => {
    const handleRead = () => {
      setStatus(current => (current === 'unread' || current === 'expired' ? 'read' : current));
    };

    window.addEventListener(READ_EVENT, handleRead);

    return () => {
      window.removeEventListener(READ_EVENT, handleRead);
    };
  }, []);

  // Everything shown is derived from the content and the status, so it cannot drift from either.
  const showsMessage = !ended && (status === 'unread' || status === 'read');
  const messageCount = status === 'unread' ? 1 : 0;
  const title = showsMessage ? messageTitle ?? '' : '';
  const text = showsMessage ? messageText ?? '' : emptyText ?? '';
  const link = showsMessage ? messageLink ?? '' : '';
  const buttonLabel = showsMessage ? linkLabel ?? '' : '';

  const markAsRead = useCallback(() => {
    setStatus(current => (current === 'unread' || current === 'expired' ? 'read' : current));
    window.dispatchEvent(new Event(READ_EVENT));

    const startTime = convertCESTStringToDate(startDate);
    const endTime = convertCESTStringToDate(endDate);

    if (startTime && endTime && title && text && link && buttonLabel) {
      const readMessageInfo: LocalStorageMessageType = {
        startTime: startTime.getTime(),
        endTime: endTime.getTime(),
        messageTitle: title,
        messageText: text,
        messageLink: link,
        linkLabel: buttonLabel,
      };

      if (storageAvailable('localStorage')) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(readMessageInfo));
      }
    }
  }, [buttonLabel, endDate, link, startDate, text, title]);

  return { messageCount, status, title, text, link, buttonLabel, markAsRead };
};
